import React from "react";
import { FaPlus, FaEdit, FaTrash, FaLayerGroup } from "react-icons/fa";
import { ALL_TEMPLATES, NO_TEMPLATE } from "../../../utils/templateGrouping";

/**
 * ŞABLON SÜTUNU (#298) — Kullanıcılar / Çalışanlar sekmesinin solundaki şablon
 * listesi. Şablona tıklamak sağdaki listeyi o şablondakilere süzer; seçili
 * şablonun özeti (`renderSummary`) sütunda açılır. Düzenle / sil / yeni şablon
 * çağıranın çekmecesine ve onayına gider (bu bileşen yalnız gösterir).
 *
 * Masaüstü: solda sabit sütun. Mobil: listenin üstünde yatay çipler + seçili
 * şablonun düzenle/sil düğmeleri.
 *
 * Renk `accent` ile: "indigo" (kullanıcı) | "orange" (çalışan).
 */
const ACCENTS = {
  indigo: {
    icon: "text-indigo-500",
    iconBg: "bg-indigo-50",
    selected: "bg-white border-indigo-200 shadow-sm",
    selectedText: "text-indigo-700",
    count: "bg-indigo-100 text-indigo-700",
    chip: "bg-indigo-600 text-white border-indigo-600",
    add: "text-indigo-600 hover:bg-indigo-50",
  },
  orange: {
    icon: "text-orange-500",
    iconBg: "bg-orange-50",
    selected: "bg-white border-orange-200 shadow-sm",
    selectedText: "text-orange-700",
    count: "bg-orange-100 text-orange-700",
    chip: "bg-orange-500 text-white border-orange-500",
    add: "text-orange-600 hover:bg-orange-50",
  },
};

const IconButton = ({ onClick, title, danger, children }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    title={title}
    aria-label={title}
    className={`p-1.5 rounded-lg text-gray-400 transition-colors ${
      danger ? "hover:text-fifth hover:bg-red-50" : "hover:text-main hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

export default function TemplateRail({
  title,
  accent = "indigo",
  templates = [],
  stats = { total: 0, unassigned: 0, counts: {} },
  selectedId = ALL_TEMPLATES,
  onSelect,
  loading = false,
  canCreate = false,
  onCreate,
  canEdit = false,
  onEdit,
  canDeleteTemplate = () => false,
  onDelete,
  renderSummary,
}) {
  const a = ACCENTS[accent] || ACCENTS.indigo;
  const selectedTemplate = templates.find((t) => String(t._id) === String(selectedId)) || null;

  const rows = [
    { key: "all", id: ALL_TEMPLATES, name: "Tümü", count: stats.total },
    ...templates.map((t) => ({
      key: String(t._id),
      id: String(t._id),
      name: t.name,
      count: stats.counts?.[String(t._id)] ?? 0,
      template: t,
    })),
    // Şablonsuz satırı yalnız böyle biri varsa — boş satır gürültü olur.
    ...(stats.unassigned > 0
      ? [{ key: "none", id: NO_TEMPLATE, name: "Şablonsuz", count: stats.unassigned, muted: true }]
      : []),
  ];
  const isSelected = (row) => (row.id === ALL_TEMPLATES ? selectedId == null : String(row.id) === String(selectedId));

  const actions = (t) => (
    <>
      {canEdit && <IconButton onClick={() => onEdit(t)} title="Şablonu düzenle"><FaEdit className="w-3 h-3" /></IconButton>}
      {canDeleteTemplate(t) && (
        <IconButton onClick={() => onDelete(t)} title="Şablonu sil" danger>
          <FaTrash className="w-3 h-3" />
        </IconButton>
      )}
    </>
  );

  return (
    <>
      {/* ─── Masaüstü: sol sütun ─── */}
      <aside className="hidden md:flex md:flex-col w-64 lg:w-72 shrink-0 border-r border-gray-200 bg-gray-50/60 min-h-0">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-white">
          <div className={`w-8 h-8 ${a.iconBg} rounded-lg flex items-center justify-center shrink-0`}>
            <FaLayerGroup className={`w-3.5 h-3.5 ${a.icon}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-Bold text-gray-800 truncate">{title}</p>
            <p className="text-[11px] text-gray-400">{templates.length} şablon</p>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={onCreate}
              title="Yeni şablon"
              aria-label="Yeni şablon"
              className={`p-2 rounded-lg ${a.add} transition-colors`}
            >
              <FaPlus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loading
            ? [1, 2, 3, 4].map((i) => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)
            : rows.map((row) => {
                const active = isSelected(row);
                return (
                  <div
                    key={row.key}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(row.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(row.id);
                      }
                    }}
                    aria-pressed={active}
                    className={`group rounded-xl border px-3 py-2 cursor-pointer transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
                      active ? a.selected : "border-transparent hover:bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex-1 min-w-0 truncate text-sm ${
                          active ? `font-SemiBold ${a.selectedText}` : row.muted ? "text-gray-400 italic" : "text-gray-700"
                        }`}
                      >
                        {row.name}
                      </span>
                      {row.template && (
                        <span className={`flex items-center ${active ? "" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"}`}>
                          {actions(row.template)}
                        </span>
                      )}
                      <span
                        className={`min-w-[1.75rem] text-center px-1.5 py-0.5 rounded-md text-[11px] font-SemiBold tabular-nums ${
                          active ? a.count : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {row.count}
                      </span>
                    </div>
                    {active && row.template && renderSummary && (
                      <div className="mt-2 pt-2 border-t border-gray-100">{renderSummary(row.template)}</div>
                    )}
                  </div>
                );
              })}
          {!loading && templates.length === 0 && (
            <p className="px-3 py-4 text-xs text-gray-400 text-center">Henüz şablon tanımlanmamış.</p>
          )}
        </div>
      </aside>

      {/* ─── Mobil: yatay çipler ─── */}
      <div className="md:hidden border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar px-4 py-2">
          {rows.map((row) => {
            const active = isSelected(row);
            return (
              <button
                key={row.key}
                type="button"
                onClick={() => onSelect(row.id)}
                aria-pressed={active}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs whitespace-nowrap transition-colors ${
                  active ? a.chip : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                <span className={row.muted && !active ? "italic" : ""}>{row.name}</span>
                <span className={`tabular-nums ${active ? "opacity-90" : "text-gray-400"}`}>{row.count}</span>
              </button>
            );
          })}
          {canCreate && (
            <button
              type="button"
              onClick={onCreate}
              aria-label="Yeni şablon"
              className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-gray-300 text-xs ${a.add}`}
            >
              <FaPlus className="w-2.5 h-2.5" /> Şablon
            </button>
          )}
        </div>
        {selectedTemplate && (renderSummary || canEdit || canDeleteTemplate(selectedTemplate)) && (
          <div className="flex items-start gap-2 px-4 pb-2">
            <div className="flex-1 min-w-0">{renderSummary?.(selectedTemplate)}</div>
            <div className="flex items-center shrink-0">{actions(selectedTemplate)}</div>
          </div>
        )}
      </div>
    </>
  );
}
